import React, { useState } from 'react';
import { Problem, Operation } from './types';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';

interface ProblemTableProps {
  operation: Operation;
  problems: Problem[];
  showAnswers: boolean[];
  toggleAnswer: (operation: Operation, index: number) => void;
  getOperationSymbol: (op: Operation) => string;
  calculateAnswer: (problem: Problem) => string | number;
  highlight?: boolean;
  currentLevel?: number;
  currentSeed?: string;
}

const ProblemTable: React.FC<ProblemTableProps> = ({
  operation,
  problems,
  showAnswers,
  toggleAnswer,
  getOperationSymbol,
  calculateAnswer,
  highlight = false,
  currentLevel,
  currentSeed,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyLink = async () => {
    // Convert array index to Kyu number: index 0 -> 15th Kyu, index 14 -> 1st Kyu
    const levelNum = 15 - (currentLevel || 0);
    const url = `${window.location.origin}/${levelNum}/${operation}${currentSeed ? `/${currentSeed}` : ''}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <Card 
      data-testid={`problem-table-${operation}`}
      className={highlight ? "ring-2 ring-blue-500 ring-offset-2" : ""}
    >
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{operation.charAt(0).toUpperCase() + operation.slice(1)}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={copyLink}
            className="text-xs"
          >
            {copySuccess ? '✓ Copied!' : '🔗 Copy Link'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label={`${operation} practice problems`}>
            <caption className="sr-only">
              {operation.charAt(0).toUpperCase() + operation.slice(1)} problems - Click answer cells to reveal solutions
            </caption>
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 font-medium" scope="col">#</th>
                <th className="text-left p-2 font-medium" scope="col">Problem</th>
                <th className="text-left p-2 font-medium" scope="col">Answer</th>
              </tr>
            </thead>
            <tbody>
              {problems.map((problem, index) => (
                <tr key={index} className="border-b hover:bg-muted/50">
                  <td className="p-2 text-muted-foreground">
                    {index + 1}
                  </td>
                  <td className="p-2 font-mono">
                    {problem.num1} {getOperationSymbol(problem.operation)} {problem.num2} =
                  </td>
                  <td className="p-2">
                    <Button
                      variant={showAnswers[index] ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => toggleAnswer(operation, index)}
                      aria-label={showAnswers[index] ? `Answer: ${calculateAnswer(problem)}` : 'Show answer'}
                      aria-pressed={showAnswers[index]}
                      className="font-mono answer-toggle-btn"
                      data-testid={`toggle-${operation}-${index}`}
                    >
                      {showAnswers[index] ? calculateAnswer(problem) : 'Show'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProblemTable;
